import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Divider,
  Group,
  Modal,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconScale } from "@tabler/icons-react";
import { getFullDate } from "../../../lib/methods";
import {
  cancelUwipMembership,
  getUwipMemberships,
  IUwipMembership,
  markUwipZellePaid,
} from "../../../services/uwip";

const statusColor = (status: string) => {
  switch (status) {
    case "active":
    case "trialing":
      return "teal";
    case "zelle_pending":
    case "checkout_pending":
      return "yellow";
    case "past_due":
      return "orange";
    case "canceled":
      return "gray";
    default:
      return "gray";
  }
};

const formatMoney = (cents?: number) => {
  if (typeof cents !== "number") return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
};

const UwipMembers: React.FC = () => {
  const [memberships, setMemberships] = useState<IUwipMembership[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [foundingRemaining, setFoundingRemaining] = useState<number>();
  const [foundingCap, setFoundingCap] = useState<number>(20);
  const [selected, setSelected] = useState<IUwipMembership | null>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const limit = 10;

  useEffect(() => {
    loadMemberships();
  }, [page, statusFilter]);

  async function loadMemberships() {
    setLoading(true);
    try {
      const result = await getUwipMemberships(
        page,
        limit,
        statusFilter || undefined
      );
      setMemberships(result.data || []);
      setTotalPages(Math.ceil((result.totalRecords || 0) / limit) || 1);
      setFoundingRemaining(result.foundingRemaining);
      if (result.foundingCap) setFoundingCap(result.foundingCap);
    } catch (error) {
      console.error("Failed to load U-WIP memberships:", error);
    }
    setLoading(false);
  }

  const handleCancel = async (id: string) => {
    if (
      !window.confirm(
        "Cancel this membership at period end? No refund will be issued."
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      await cancelUwipMembership(id);
      setModalOpened(false);
      await loadMemberships();
    } catch (error) {
      console.error("Failed to cancel membership:", error);
    }
    setLoading(false);
  };

  const handleZellePaid = async (id: string) => {
    if (!window.confirm("Mark this Zelle payment as received?")) return;
    setLoading(true);
    try {
      const updated = await markUwipZellePaid(id);
      setSelected(updated);
      await loadMemberships();
    } catch (error) {
      console.error("Failed to confirm Zelle payment:", error);
    }
    setLoading(false);
  };

  const rows = memberships.map((membership) => (
    <Table.Tr
      key={membership.id}
      onClick={() => {
        setSelected(membership);
        setModalOpened(true);
      }}
      style={{ cursor: "pointer" }}
    >
      <Table.Td>
        <Text fw={500}>
          {membership.firstName} {membership.lastName}
        </Text>
        <Text size="xs" c="dimmed">
          {membership.email}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{membership.plan}</Text>
      </Table.Td>
      <Table.Td>
        <Badge color={statusColor(membership.status)} variant="light">
          {membership.status.replace("_", " ")}
        </Badge>
      </Table.Td>
      <Table.Td>
        {membership.isFoundingMember ? (
          <Badge color="teal">#{membership.foundingNumber}</Badge>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm">{membership.paymentMethod}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {membership.currentPeriodEnd
            ? getFullDate(membership.currentPeriodEnd)
            : membership.trialEndsAt
            ? getFullDate(membership.trialEndsAt)
            : "—"}
        </Text>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div>
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2} mb="xs">
              U-WIP Members
            </Title>
            <Text c="dimmed">
              Memberships, consent, founding pricing, and Zelle confirmations
            </Text>
          </div>
          <Badge
            size="lg"
            color="teal"
            variant="light"
            leftSection={<IconScale size={14} />}
          >
            Founding slots {foundingRemaining ?? "—"}/{foundingCap}
          </Badge>
        </Group>

        <Select
          placeholder="Filter by status"
          data={[
            { value: "", label: "All statuses" },
            { value: "trialing", label: "Trialing" },
            { value: "active", label: "Active" },
            { value: "zelle_pending", label: "Zelle pending" },
            { value: "checkout_pending", label: "Checkout pending" },
            { value: "past_due", label: "Past due" },
            { value: "canceled", label: "Canceled" },
          ]}
          value={statusFilter}
          onChange={(value) => {
            setPage(1);
            setStatusFilter(value || "");
          }}
          clearable
          maw={260}
        />

        <Paper p="md" radius="md" shadow="xs" withBorder>
          {loading && memberships.length === 0 ? (
            <Stack gap="xs">
              <Skeleton height={50} />
              <Skeleton height={50} />
              <Skeleton height={50} />
            </Stack>
          ) : memberships.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No U-WIP memberships yet
            </Text>
          ) : (
            <ScrollArea>
              <Table highlightOnHover striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Member</Table.Th>
                    <Table.Th>Plan</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Founding</Table.Th>
                    <Table.Th>Payment</Table.Th>
                    <Table.Th>Period / trial end</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
            </ScrollArea>
          )}

          {totalPages > 1 && (
            <Group justify="center" mt="xl">
              <Pagination
                value={page}
                onChange={setPage}
                total={totalPages}
                size="sm"
                radius="md"
              />
            </Group>
          )}
        </Paper>
      </Stack>

      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title="U-WIP membership"
        size="lg"
        centered
      >
        {selected && (
          <Stack gap="md">
            <div>
              <Title order={4}>
                {selected.firstName} {selected.lastName}
              </Title>
              <Text size="sm">{selected.email}</Text>
              <Text size="sm">{selected.phone}</Text>
              {selected.birthYear && (
                <Text size="sm" c="dimmed">
                  Birth year: {selected.birthYear}
                </Text>
              )}
              {selected.preferredContactTime && (
                <Text size="sm" c="dimmed">
                  Preferred contact time: {selected.preferredContactTime}
                </Text>
              )}
            </div>
            <Group>
              <Badge color={statusColor(selected.status)}>
                {selected.status.replace("_", " ")}
              </Badge>
              <Badge variant="outline">{selected.plan}</Badge>
              {selected.isFoundingMember && (
                <Badge color="teal">Founding #{selected.foundingNumber}</Badge>
              )}
              {selected.cancelAtPeriodEnd && (
                <Badge color="orange">Cancels at period end</Badge>
              )}
            </Group>
            <Divider />
            <Text size="sm">
              Consent v{selected.consentVersion}
              {selected.consentAcceptedAt
                ? ` · ${getFullDate(selected.consentAcceptedAt)}`
                : ""}
            </Text>
            {selected.stripeSubscriptionId && (
              <Text size="xs" c="dimmed" ff="monospace">
                Stripe sub: {selected.stripeSubscriptionId}
              </Text>
            )}
            {selected.payments && selected.payments.length > 0 && (
              <>
                <Divider label="Payments" />
                {selected.payments.map((payment, index) => (
                  <Group key={`${payment.stripeInvoiceId || index}`} justify="space-between">
                    <Text size="sm">{payment.description || payment.status}</Text>
                    <Text size="sm">{formatMoney(payment.amountCents)}</Text>
                  </Group>
                ))}
              </>
            )}
            <Group>
              {selected.status === "zelle_pending" && (
                <Button
                  color="teal"
                  onClick={() => handleZellePaid(selected.id)}
                  loading={loading}
                >
                  Mark Zelle paid
                </Button>
              )}
              {selected.status !== "canceled" && (
                <Button
                  variant="outline"
                  color="red"
                  onClick={() => handleCancel(selected.id)}
                  loading={loading}
                >
                  Cancel at period end
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
};

export default UwipMembers;
